import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import '../../../styles/commerciale.css';

interface Sconto {
  id: number;
  societaId: number;
  societa: { id: number; nome: string };
  codice: string;
  descrizione: string | null;
  percentualeEquivalente: number;
  moltiplicatore: number;
  attivo: boolean;
}
interface Preview {
  valida: boolean;
  percentualeEquivalente?: number;
  moltiplicatore?: number;
  errore?: string;
}

const CHIPS = [5, 10, 15, 20, 25, 30, 33, 40, 50];

function fmt(n: number, decimals = 2) {
  return n.toFixed(decimals).replace('.', ',');
}
function getBarColor(pct: number): string {
  if (pct <= 25) return '#22c55e';
  if (pct <= 50) return '#eab308';
  if (pct <= 75) return '#f97316';
  return '#ef4444';
}

export default function ScontiPage() {
  // ── Data ────────────────────────────────────────────────────────────────
  const [sconti, setSconti] = useState<Sconto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // ── Insert form ──────────────────────────────────────────────────────────
  const [stringa, setStringa] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [insertSocietaId, setInsertSocietaId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [prezzoSim, setPrezzoSim] = useState('100');
  const [isCalculating, setIsCalculating] = useState(false);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterSearch, setFilterSearch] = useState('');
  const [filterPctMin, setFilterPctMin] = useState('');
  const [filterPctMax, setFilterPctMax] = useState('');
  const [filterDisattivati, setFilterDisattivati] = useState(false);

  // ── UX ───────────────────────────────────────────────────────────────────
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // ── Modal modifica ────────────────────────────────────────────────────────
  const [editSconto, setEditSconto] = useState<Sconto | null>(null);
  const [editStringa, setEditStringa] = useState('');
  const [editDescrizione, setEditDescrizione] = useState('');
  const [editPreview, setEditPreview] = useState<Preview | null>(null);
  const [editPrezzoSim, setEditPrezzoSim] = useState('100');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const filterTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);
  const editPreviewTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ── Società di destinazione del nuovo sconto ──────────────────────────────
  // Quando la topbar ha una società selezionata (isUnica) la combo società non
  // viene renderizzata: la destinazione è SEMPRE quella della topbar.
  // Non usare mai societaList[0] come fallback — scriverebbe in un'altra società.
  const sidInsert = isUnica ? filterSocietaId : insertSocietaId;
  const nomeSocietaInsert = societaList.find(s => String(s.id) === sidInsert)?.nome ?? '';

  // ── Fetch sconti ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(fetchSconti, 300);
    return () => { if (filterTimerRef.current) clearTimeout(filterTimerRef.current); };
  }, [pronta, filterSocietaId, filterSearch, filterPctMin, filterPctMax, filterDisattivati]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSconti = async () => {
    if (!pronta) return;
    try {
      setIsLoading(true);
      const p = new URLSearchParams();
      if (filterSocietaId) p.append('societaId', filterSocietaId);
      if (filterSearch) p.append('search', filterSearch);
      if (filterPctMin) p.append('percentualeMin', filterPctMin);
      if (filterPctMax) p.append('percentualeMax', filterPctMax);
      if (filterDisattivati) p.append('mostraDisattivati', 'true');
      const data = await apiFetch<Sconto[]>(`/api/sconti?${p}`);
      setSconti(data);
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  // ── Preview debounce (insert) ─────────────────────────────────────────────
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (!stringa.trim()) { setPreview(null); return; }
    previewTimerRef.current = setTimeout(async () => {
      setIsCalculating(true);
      try {
        const data = await apiFetch<Preview>('/api/sconti/calcola', {
          method: 'POST', body: JSON.stringify({ stringa }),
        });
        setPreview(data);
      } catch (err: any) { setPreview({ valida: false, errore: err.message || 'Errore di calcolo' }); }
      finally { setIsCalculating(false); }
    }, 300);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [stringa]);

  // ── Preview debounce (edit) ───────────────────────────────────────────────
  useEffect(() => {
    if (editPreviewTimerRef.current) clearTimeout(editPreviewTimerRef.current);
    if (!editStringa.trim()) { setEditPreview(null); return; }
    editPreviewTimerRef.current = setTimeout(async () => {
      try {
        const data = await apiFetch<Preview>('/api/sconti/calcola', {
          method: 'POST', body: JSON.stringify({ stringa: editStringa }),
        });
        setEditPreview(data);
      } catch (err: any) { setEditPreview({ valida: false, errore: err.message || 'Errore di calcolo' }); }
    }, 300);
    return () => { if (editPreviewTimerRef.current) clearTimeout(editPreviewTimerRef.current); };
  }, [editStringa]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const appendChip = (val: number) => {
    setStringa(prev => prev.trim() ? `${prev.trim()}+${val}` : `${val}`);
  };
  const appendEditChip = (val: number) => {
    setEditStringa(prev => prev.trim() ? `${prev.trim()}+${val}` : `${val}`);
  };

  const highlight = (id: number) => {
    setHighlightId(id);
    setTimeout(() => setHighlightId(null), 2000);
  };

  // ── Salva nuovo ───────────────────────────────────────────────────────────
  const handleSalva = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview?.valida) return;
    if (!sidInsert) { showToast('Seleziona la società', 'error'); return; }
    try {
      setIsSaving(true);
      const nuovo = await apiFetch<Sconto>('/api/sconti', {
        method: 'POST',
        body: JSON.stringify({
          societaId: parseInt(sidInsert, 10),
          codice: stringa.trim(),
          descrizione: descrizione.trim() || undefined,
        }),
      });
      setStringa(''); setDescrizione(''); setPreview(null);
      setSconti(prev => [nuovo, ...prev]);
      highlight(nuovo.id);
      showToast('Sconto salvato con successo!');
    } catch (err: any) { showToast(err.message || 'Errore durante il salvataggio', 'error'); }
    finally { setIsSaving(false); }
  };

  // ── Duplica ───────────────────────────────────────────────────────────────
  const handleDuplica = async (s: Sconto) => {
    try {
      const nuovo = await apiFetch<Sconto>(`/api/sconti/${s.id}/duplica`, { method: 'POST' });
      setSconti(prev => [nuovo, ...prev]);
      highlight(nuovo.id);
      showToast('Sconto duplicato!');
    } catch (err: any) { showToast(err.message || 'Errore duplicazione', 'error'); }
  };

  // ── Disattiva ─────────────────────────────────────────────────────────────
  const handleDisattiva = async (s: Sconto) => {
    if (!window.confirm(`Disattivare lo sconto "${s.codice}"?`)) return;
    try {
      await apiFetch(`/api/sconti/${s.id}`, { method: 'DELETE' });
      setSconti(prev => prev.map(x => x.id === s.id ? { ...x, attivo: false } : x));
      showToast('Sconto disattivato.');
    } catch (err: any) { showToast(err.message || 'Errore', 'error'); }
  };

  // ── Apri modifica ─────────────────────────────────────────────────────────
  const openEdit = (s: Sconto) => {
    setEditSconto(s);
    setEditStringa(s.codice);
    setEditDescrizione(s.descrizione || '');
    setEditPreview({ valida: true, percentualeEquivalente: s.percentualeEquivalente, moltiplicatore: s.moltiplicatore });
    setEditPrezzoSim('100');
  };

  // ── Salva modifica ────────────────────────────────────────────────────────
  const handleSalvaEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSconto || !editPreview?.valida) return;
    try {
      setIsSavingEdit(true);
      const updated = await apiFetch<Sconto>(`/api/sconti/${editSconto.id}`, {
        method: 'PUT',
        body: JSON.stringify({ codice: editStringa.trim(), descrizione: editDescrizione.trim() || null }),
      });
      setSconti(prev => prev.map(x => x.id === updated.id ? updated : x));
      setEditSconto(null);
      showToast('Sconto aggiornato!');
    } catch (err: any) { showToast(err.message || 'Errore aggiornamento', 'error'); }
    finally { setIsSavingEdit(false); }
  };

  // ── Componente preview ────────────────────────────────────────────────────
  const PreviewBox = ({ p, prezzoS, setPrezzoS }: { p: Preview | null; prezzoS: string; setPrezzoS: (v: string) => void }) => {
    if (!p) return (
      <div className="commerciale-sconti-preview-empty">
        Digita una stringa per vedere il calcolo
      </div>
    );
    if (!p.valida) return (
      <div className="commerciale-sconti-preview-error">
        {p.errore}
      </div>
    );
    const pct = p.percentualeEquivalente!;
    const molt = p.moltiplicatore!;
    const simPrezzo = parseFloat(prezzoS) || 100;
    const scontato = (simPrezzo * molt).toFixed(2);
    return (
      <div className="commerciale-sconti-preview-success">
        <div className="commerciale-sconti-preview-values">
          <div>
            <div className="commerciale-sconti-preview-label">SCONTO EQUIVALENTE</div>
            <div className="commerciale-sconti-preview-value">{fmt(pct)}%</div>
          </div>
          <div>
            <div className="commerciale-sconti-preview-label">MOLTIPLICATORE</div>
            <div className="commerciale-sconti-preview-value">{fmt(molt, 4)}</div>
          </div>
        </div>
        <div className="commerciale-sconti-preview-sim">
          <span className="commerciale-sconti-preview-sim-label">Simulazione su €</span>
          <input
            type="number" value={prezzoS} onChange={e => setPrezzoS(e.target.value)}
            className="commerciale-sconti-preview-sim-input"
          />
          <span className="commerciale-sconti-preview-sim-result">→ €{scontato}</span>
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout title="Sconti">

      {/* ── Toast ── */}
      {toast && (
        <div className={`commerciale-sconti-toast ${toast.type}`}>
          {toast.msg}
        </div>
      )}

      {/* ── INSERIMENTO RAPIDO ── */}
      <div className="bop-card">
        <h2 className="commerciale-sconti-section-title">Inserimento Rapido</h2>
        <form onSubmit={handleSalva}>
          <div className="commerciale-sconti-form-grid">

            {/* Colonna sinistra: form */}
            <div className="commerciale-sconti-form-col">

              {/* Input stringa */}
              <div>
                <label className="bop-label">Stringa sconto *</label>
                <div className="commerciale-sconti-input-group">
                  <input
                    type="text" value={stringa}
                    onChange={e => setStringa(e.target.value)}
                    placeholder="es. 50+10+10"
                    className="bop-input commerciale-sconti-input-mono"
                  />
                  {stringa && (
                    <button type="button" onClick={() => { setStringa(''); setPreview(null); }}
                      className="commerciale-sconti-clear-btn">
                      ×
                    </button>
                  )}
                </div>
                {/* Mattoncini */}
                <div className="commerciale-sconti-chips">
                  {CHIPS.map(v => (
                    <button key={v} type="button" onClick={() => appendChip(v)}
                      className="commerciale-sconti-chip">
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Descrizione */}
              <div>
                <label className="bop-label">Descrizione (opzionale)</label>
                <input type="text" value={descrizione} onChange={e => setDescrizione(e.target.value)}
                  placeholder="es. Sconto standard clienti A"
                  className="bop-input" />
              </div>

              {/* Società */}
              {!isUnica && (
                <div>
                  <label className="bop-label">Società *</label>
                  <select value={insertSocietaId} onChange={e => setInsertSocietaId(e.target.value)} className="bop-select" required>
                    <option value="">Seleziona...</option>
                    {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              <button type="submit"
                disabled={isSaving || !preview?.valida || isCalculating}
                className="commerciale-sconti-submit-btn">
                {isSaving ? 'Salvataggio...' : 'Salva Sconto'}
              </button>

              {isUnica && nomeSocietaInsert && (
                <p className="bop-text-muted commerciale-sconti-societa-hint">
                  Lo sconto verrà creato nella società <strong>{nomeSocietaInsert}</strong>.
                </p>
              )}
            </div>

            {/* Colonna destra: preview */}
            <div>
              <label className="bop-label">Anteprima calcolo</label>
              <PreviewBox p={isCalculating ? null : preview} prezzoS={prezzoSim} setPrezzoS={setPrezzoSim} />
            </div>
          </div>
        </form>
      </div>

      {/* ── GRIGLIA SCONTI ── */}
      <div className="bop-card">
        <div className="commerciale-sconti-archive-header">
          <h2 className="commerciale-sconti-section-title">Archivio Sconti</h2>
          <div className="commerciale-sconti-filters">
            {!isUnica && (
              <select value={filterSocietaId} onChange={e => setFilterSocietaId(e.target.value)}
                className="commerciale-sconti-filter-select">
                <option value="">Tutte le società</option>
                {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            )}
            <input type="text" placeholder="Cerca..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
              className="commerciale-sconti-filter-input" />
            <input type="number" placeholder="Da %" value={filterPctMin} onChange={e => setFilterPctMin(e.target.value)}
              className="commerciale-sconti-filter-input commerciale-sconti-filter-input-sm" />
            <input type="number" placeholder="A %" value={filterPctMax} onChange={e => setFilterPctMax(e.target.value)}
              className="commerciale-sconti-filter-input commerciale-sconti-filter-input-sm" />
            <label className="commerciale-filtro-checkbox">
              <input type="checkbox" checked={filterDisattivati} onChange={e => setFilterDisattivati(e.target.checked)} />
              Mostra disattivati
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="commerciale-sconti-loading">Caricamento...</div>
        ) : sconti.length === 0 ? (
          <div className="commerciale-sconti-empty">
            Nessuno sconto trovato.
          </div>
        ) : (
          <div className="commerciale-sconti-table-wrap">
            <table className="commerciale-sconti-table">
              <thead>
                <tr>
                  {['Codice', 'Descrizione', '% Equivalente', 'Moltiplicatore', '€100 →', 'Stato', 'Azioni'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sconti.map(s => {
                  const isHighlighted = s.id === highlightId;
                  const pct = s.percentualeEquivalente;
                  return (
                    <tr key={s.id} className={isHighlighted ? 'highlight' : ''}>
                      <td className="code">
                        {s.codice}
                      </td>
                      <td className="description">
                        {s.descrizione || <em>—</em>}
                      </td>
                      <td className="percentage">
                        <div className="commerciale-sconti-percentage-value" style={{ color: getBarColor(pct) }}>
                          {fmt(pct)}%
                        </div>
                        <div className="commerciale-sconti-percentage-bar-bg">
                          <div className="commerciale-sconti-percentage-bar" style={{ width: `${Math.min(pct, 100)}%`, background: getBarColor(pct) }} />
                        </div>
                      </td>
                      <td className="multiplier">
                        {fmt(s.moltiplicatore, 4)}
                      </td>
                      <td className="price">
                        €{(100 * s.moltiplicatore).toFixed(2).replace('.', ',')}
                      </td>
                      <td>
                        <span className={`commerciale-sconti-badge ${s.attivo ? 'active' : 'inactive'}`}>
                          {s.attivo ? 'Attivo' : 'Disattivo'}
                        </span>
                      </td>
                      <td>
                        <div className="commerciale-sconti-actions">
                          <button onClick={() => openEdit(s)}
                            className="commerciale-sconti-btn commerciale-sconti-btn-edit">
                            Modifica
                          </button>
                          <button onClick={() => handleDuplica(s)}
                            className="commerciale-sconti-btn commerciale-sconti-btn-duplicate">
                            Duplica
                          </button>
                          {s.attivo && (
                            <button onClick={() => handleDisattiva(s)}
                              className="commerciale-sconti-btn commerciale-sconti-btn-deactivate">
                              Disattiva
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Modifica ── */}
      {editSconto && (
        <div className="commerciale-sconti-modal-overlay">
          <div className="commerciale-sconti-modal">
            <div className="commerciale-sconti-modal-header">
              <h2 className="commerciale-sconti-modal-title">Modifica Sconto</h2>
              <button onClick={() => setEditSconto(null)} className="commerciale-sconti-modal-close">×</button>
            </div>
            <form onSubmit={handleSalvaEdit}>
              <div className="commerciale-sconti-form-grid">
                <div className="commerciale-sconti-form-col">
                  <div>
                    <label className="bop-label">Stringa sconto *</label>
                    <div className="commerciale-sconti-input-group">
                      <input type="text" value={editStringa} onChange={e => setEditStringa(e.target.value)}
                        placeholder="es. 50+10+10"
                        className="bop-input commerciale-sconti-input-mono" />
                      {editStringa && (
                        <button type="button" onClick={() => { setEditStringa(''); setEditPreview(null); }}
                          className="commerciale-sconti-clear-btn">
                          ×
                        </button>
                      )}
                    </div>
                    <div className="commerciale-sconti-chips">
                      {CHIPS.map(v => (
                        <button key={v} type="button" onClick={() => appendEditChip(v)}
                          className="commerciale-sconti-chip">
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="bop-label">Descrizione (opzionale)</label>
                    <input type="text" value={editDescrizione} onChange={e => setEditDescrizione(e.target.value)}
                      className="bop-input" />
                  </div>
                  <div className="commerciale-sconti-modal-actions">
                    <button type="button" onClick={() => setEditSconto(null)}
                      className="commerciale-sconti-modal-btn-cancel">
                      Annulla
                    </button>
                    <button type="submit" disabled={isSavingEdit || !editPreview?.valida}
                      className="commerciale-sconti-modal-btn-save">
                      {isSavingEdit ? 'Salvataggio...' : 'Salva'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="bop-label">Anteprima</label>
                  <PreviewBox p={editPreview} prezzoS={editPrezzoSim} setPrezzoS={setEditPrezzoSim} />
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
